import React, { useState } from 'react';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOffTwoToneIcon from '@mui/icons-material/VisibilityOffTwoTone';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';
import {
  Button,
  Alert,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { PASSWORD_REGEX } from '../../utils/constants';
import axiosInstance from '../../api';
import StyledCardNoHeight from '../../components/card/StyledCardNoHeight';
import SERVER_API_URL from '../../config';
import { INTERNAL_SERVER_ERROR, PASSWORD_EXCEPTION, REQUIRED_FIELD } from '../../utils/exception';

export default function PasswordChange({ username, canEdit }) {
  const [formError, setFormError] = useState(null);
  const [formErrorSeverity, setFormErrorSeverity] = useState('error');

  const [disabled, setDisabled] = useState(false);
  const [firstDisabled, setFirstDisabled] = useState(true);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [currentPasswordClicked, setCurrentPasswordClicked] = useState(false);
  const [formErrorOnCurrentPassword, setFormErrorOnCurrentPassword] = useState(false);
  const [helperTextOnCurrentPassword, setHelperTextOnCurrentPassword] = useState(null);

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newPasswordClicked, setNewPasswordClicked] = useState(false);
  const [formErrorOnNewPassword, setFormErrorOnNewPassword] = useState(false);
  const [helperTextOnNewPassword, setHelperTextOnNewPassword] = useState(null);

  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordClicked, setConfirmPasswordClicked] = useState(false);
  const [formErrorOnConfirmPassword, setFormErrorOnConfirmPassword] = useState(false);
  const [helperTextOnConfirmPassword, setHelperTextOnConfirmPassword] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormErrorSeverity('error');
    setDisabled(true);

    if (!formErrorOnCurrentPassword && !formErrorOnNewPassword && !formErrorOnConfirmPassword) {
      const { currentPassword, newPassword, confirmPassword } = event.target.elements;
      if (newPassword.value === confirmPassword.value) {
        try {
          const response = await axiosInstance.patch(`${SERVER_API_URL}/auth/changePassword`, {
            username,
            currentPassword: currentPassword.value,
            newPassword: newPassword.value,
          });
          if (response.status === 200) {
            setFormError('የይለፍ ቃል በተሳካ ሁኔታ ተቀይሯል።');
            setFormErrorSeverity('success');
          }
        } catch (e) {
          if (e.response.status === 401) {
            setFormError('የአሁኑ የይለፍ ቃል የተሳሳተ ነው!');
          } else if (e.response.status === 500) {
            setFormError(INTERNAL_SERVER_ERROR);
          }
        }
      } else {
        setFormError('አዲሱን የይለፍ ቃል በትክክል ያረጋግጡ!');
      }
    }
    setFirstDisabled(true);
    setDisabled(false);
  };

  const handleFormValueBlur = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');
    setFirstDisabled(false);
    setDisabled(false);

    const { name, value } = e.target;
    switch (name) {
      case 'currentPassword':
        if (value.length === 0) {
          setCurrentPasswordClicked(false);
          setFormErrorOnCurrentPassword(currentPasswordClicked);
          setHelperTextOnCurrentPassword(REQUIRED_FIELD);
        } else {
          setHelperTextOnCurrentPassword(null);
          setFormErrorOnCurrentPassword(false);
        }
        break;
      case 'newPassword':
        if (value.length === 0) {
          setNewPasswordClicked(false);
          setFormErrorOnNewPassword(newPasswordClicked);
          setHelperTextOnNewPassword(REQUIRED_FIELD);
        } else if (!PASSWORD_REGEX.test(value)) {
          setFormErrorOnNewPassword(true);
          setHelperTextOnNewPassword(PASSWORD_EXCEPTION);
        } else {
          setHelperTextOnNewPassword(null);
          setFormErrorOnNewPassword(false);
        }
        break;
      case 'confirmPassword':
        if (value.length === 0) {
          setConfirmPasswordClicked(false);
          setFormErrorOnConfirmPassword(confirmPasswordClicked);
          setHelperTextOnConfirmPassword(REQUIRED_FIELD);
        } else if (!PASSWORD_REGEX.test(value)) {
          setFormErrorOnConfirmPassword(true);
          setHelperTextOnConfirmPassword(PASSWORD_EXCEPTION);
        } else {
          setHelperTextOnConfirmPassword(null);
          setFormErrorOnConfirmPassword(false);
        }
        break;
      default:
        break;
    }
  };

  const handleFormValueChange = (e) => {
    e.preventDefault();
    setFormError(null);
    setFormErrorSeverity('error');

    const { name, value } = e.target;
    switch (name) {
      case 'currentPassword':
        setFormErrorOnCurrentPassword(false);
        setHelperTextOnCurrentPassword(null);
        break;
      case 'newPassword':
        setFormErrorOnNewPassword(false);
        setHelperTextOnNewPassword(null);
        break;
      case 'confirmPassword':
        if (value.length === 0) {
          setConfirmPasswordClicked(false);
          setFormErrorOnConfirmPassword(confirmPasswordClicked);
          setHelperTextOnConfirmPassword(REQUIRED_FIELD);
        } else if (!PASSWORD_REGEX.test(value)) {
          setFormErrorOnConfirmPassword(true);
          setHelperTextOnConfirmPassword(PASSWORD_EXCEPTION);
        } else {
          setFormErrorOnConfirmPassword(false);
          setHelperTextOnConfirmPassword(null);
        }
        break;
      default:
        break;
    }
  };

  const handleClearForm = () => {
    setFormError(null);
    setFormErrorSeverity('error');

    setFirstDisabled(true);
    setDisabled(false);

    setCurrentPasswordClicked(false);
    setFormErrorOnCurrentPassword(false);
    setHelperTextOnCurrentPassword(null);

    setNewPasswordClicked(false);
    setFormErrorOnNewPassword(false);
    setHelperTextOnNewPassword(null);

    setConfirmPasswordClicked(false);
    setFormErrorOnConfirmPassword(false);
    setHelperTextOnConfirmPassword(null);
  };
  return (
    <>
      <StyledCardNoHeight component="form" autoComplete="off" onSubmit={handleSubmit} sx={{ p: { xs: 1, sm: 5 } }}>
        <Typography variant="h6" sx={{ color: 'darkgray' }}>
          የይለፍ ቃል ለውጥ
        </Typography>
        <Tooltip title={canEdit ? 'መገለጫህን ማረም አልተፈቀደልህም። ፍንጭ፡ እባክዎ አስተዳዳሪዎን ያነጋግሩ!' : ''}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12}>
              {formError && (
                <Alert sx={{ width: '100%' }} severity={formErrorSeverity}>
                  {formError}
                </Alert>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <input type="password" id="currentPasswordHidden" style={{ display: 'none' }} autoComplete="off" />
              <TextField
                required
                fullWidth
                error={formErrorOnCurrentPassword}
                id="currentPassword"
                name="currentPassword"
                label="የአሁኑ ሚስጥራዊ ማለፊያ ቁልፍ"
                autoComplete="off"
                // variant="standard"
                type={showCurrentPassword ? 'text' : 'password'}
                onChange={handleFormValueChange}
                helperText={formErrorOnCurrentPassword ? helperTextOnCurrentPassword : null}
                onFocus={() => setCurrentPasswordClicked(true)}
                onBlur={handleFormValueBlur}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
                        {showCurrentPassword ? <VisibilityTwoToneIcon /> : <VisibilityOffTwoToneIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={canEdit}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                required
                fullWidth
                error={formErrorOnNewPassword}
                id="newPassword"
                name="newPassword"
                label="አዲስ የይለፍ ቃል"
                // variant="standard"
                type={showNewPassword ? 'text' : 'password'}
                onChange={handleFormValueChange}
                helperText={formErrorOnNewPassword ? helperTextOnNewPassword : null}
                onFocus={() => setNewPasswordClicked(true)}
                onBlur={handleFormValueBlur}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setShowNewPassword(!showNewPassword)}>
                        {showNewPassword ? <VisibilityTwoToneIcon /> : <VisibilityOffTwoToneIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={canEdit}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField
                required
                fullWidth
                error={formErrorOnConfirmPassword}
                id="confirmPassword"
                name="confirmPassword"
                label="የይለፍ ቃል አረጋግጥ"
                // variant="standard"
                type={showConfirmPassword ? 'text' : 'password'}
                onChange={handleFormValueChange}
                helperText={formErrorOnConfirmPassword ? helperTextOnConfirmPassword : null}
                onFocus={() => setConfirmPasswordClicked(true)}
                onBlur={handleFormValueBlur}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <VisibilityTwoToneIcon /> : <VisibilityOffTwoToneIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={canEdit}
              />
            </Grid>
            <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right' }}>
              <Button
                color="error"
                type="reset"
                onClick={handleClearForm}
                sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: '30%', sm: '40%' } }}
                disabled={canEdit}
              >
                <RefreshIcon />
                ዳግም ማስጀመር
              </Button>{' '}
              {disabled && (
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ minWidth: { xs: '30%', sm: '40%' } }}
                  disabled={disabled || canEdit}
                >
                  <CircularProgress style={{ width: '20px', height: '20px' }} />
                  በማስቀመጥ ላይ
                </Button>
              )}
              {!disabled && (
                <Button
                  variant="contained"
                  type="submit"
                  sx={{ marginLeft: { xs: 2, sm: 5 }, minWidth: { xs: '30%', sm: '50%' } }}
                  startIcon={<SaveTwoToneIcon />}
                  disabled={disabled || firstDisabled || canEdit}
                >
                  አስቀምጥ
                </Button>
              )}
            </Grid>
          </Grid>
        </Tooltip>
      </StyledCardNoHeight>
    </>
  );
}

PasswordChange.propTypes = {
  username: PropTypes.string.isRequired,
  canEdit: PropTypes.bool.isRequired,
};
